<script setup lang="ts">
import { ref } from 'vue'

defineProps<{ width: number; height: number }>()
const emit = defineEmits<{
  (event: 'close'): void
  (event: 'choose-other'): void
  (event: 'confirm', remember: boolean): void
}>()
const remember = ref(false)
</script>

<template>
  <el-dialog
    :model-value="true"
    :title="$t('image2_high_resolution_title')"
    width="520px"
    align-center
    append-to-body
    :show-close="false"
    :close-on-click-modal="false"
    modal-class="enterprise-image-high-resolution-modal"
    class="enterprise-image-high-resolution-dialog"
    @close="emit('close')"
  >
    <div class="image-hd-content">
      <header class="image-hd-header">
        <span class="image-hd-icon" aria-hidden="true"><img src="/assets/images/workbench/image-expand.svg" alt="" /></span>
        <h2>{{ $t('image2_high_resolution_title') }}</h2>
        <button type="button" class="image-hd-close" :aria-label="$t('image2_high_resolution_close')" @click="emit('close')"><span aria-hidden="true" /></button>
      </header>
      <p class="image-hd-description">{{ $t('image2_high_resolution_desc', { width, height }) }}</p>
      <ul class="image-hd-risks">
        <li>{{ $t('image2_high_resolution_other_risk') }}</li>
        <li>{{ $t('image2_high_resolution_layout_risk') }}</li>
      </ul>
      <section class="image-hd-cost">
        <h3>{{ $t('image2_high_resolution_cost_title') }}</h3>
        <p>{{ $t('image2_high_resolution_cost_detail') }}</p>
        <p>{{ $t('image2_high_resolution_cost_estimate') }}</p>
        <p class="image-hd-cost-record">{{ $t('image2_high_resolution_cost_record') }}</p>
      </section>
      <label class="image-hd-remember">
        <input v-model="remember" type="checkbox" />
        <span>{{ $t('image2_high_resolution_remember') }}</span>
      </label>
      <footer class="image-hd-actions">
        <button type="button" @click="emit('choose-other')">{{ $t('image2_high_resolution_other') }}</button>
        <button type="button" class="primary" @click="emit('confirm', remember)">{{ $t('image2_high_resolution_confirm') }}</button>
      </footer>
    </div>
  </el-dialog>
</template>

<style lang="scss">
.enterprise-image-high-resolution-modal { background: rgba(31, 35, 41, .4); }
.enterprise-image-high-resolution-dialog.el-dialog {
  // main.scss globally resets dialog width/body padding with !important.
  // Restore the C-end 520px modal only for this high-resolution prompt.
  width: 520px !important;
  max-width: calc(100vw - 32px);
  box-sizing: border-box;
  padding: 0 !important;
  overflow: hidden;
  border-radius: 16px;
  box-shadow: 0 12px 40px #0003;
  .el-dialog__header { display: none; }
  .el-dialog__body {
    box-sizing: border-box;
    padding: 28px !important;
  }
}
@media (max-width: 560px) { .enterprise-image-high-resolution-dialog.el-dialog .el-dialog__body { padding: 24px 20px !important; } }
</style>

<style scoped lang="scss">
.image-hd-content { display: flex; flex-direction: column; gap: 16px; color: #1f2329; font-family: inherit; }
.image-hd-header { display: flex; align-items: center; gap: 12px; }
.image-hd-icon { display: inline-flex; align-items: center; justify-content: center; width: 40px; height: 40px; flex: 0 0 40px; background: #eaf2ff; border-radius: 10px; }
.image-hd-icon img { width: 20px; height: 20px; }
.image-hd-header h2 { flex: 1; margin: 0; font-size: 17px; line-height: 25px; font-weight: 600; }
.image-hd-close { display: flex; align-items: center; justify-content: center; width: 24px; height: 24px; flex: 0 0 24px; border: 0; background: none; cursor: pointer; padding: 3px; }
.image-hd-close span { display: block; width: 18px; height: 18px; background: #8f959e; mask: url('/assets/images/workbench/icons/x.svg') center / contain no-repeat; }
.image-hd-description { margin: 0; font-size: 14px; line-height: 24px; color: #3f444b; }
.image-hd-risks { display: flex; flex-direction: column; gap: 8px; margin: 0; padding: 0; list-style: none; font-size: 13px; line-height: 21px; color: #697077; }
.image-hd-risks li { display: flex; align-items: flex-start; gap: 8px; }
.image-hd-risks li::before { content: ''; flex: 0 0 5px; width: 5px; height: 5px; margin-top: 8px; background: #a9aeb5; border-radius: 50%; }
.image-hd-cost { display: flex; flex-direction: column; gap: 8px; padding: 14px; border-radius: 10px; background: #f0f6ff; color: #3f444b; font-size: 13px; line-height: 21px; }
.image-hd-cost h3 { font-size: 14px; font-weight: 600; color: #1f2329; margin: 0; }
.image-hd-cost p { margin: 0; }
.image-hd-cost p:nth-of-type(2) { color: #697077; }
.image-hd-cost p.image-hd-cost-record { color: #8f959e; font-size: 12px; }
.image-hd-remember { display: flex; align-items: flex-start; gap: 8px; color: #697077; font-size: 13px; line-height: 19px; cursor: pointer; }
.image-hd-remember input { appearance: none; flex: 0 0 16px; width: 16px; height: 16px; margin: 1px 0 0; border: 1px solid #c6cacf; border-radius: 4px; background: #fff; cursor: pointer; }
.image-hd-remember input:checked { background: #2b7cf6; border-color: #2b7cf6; position: relative; }
.image-hd-remember input:checked::after { content: ''; position: absolute; left: 5px; top: 2px; width: 4px; height: 8px; border: solid #fff; border-width: 0 1.5px 1.5px 0; transform: rotate(45deg); }
.image-hd-actions { display: flex; justify-content: flex-end; gap: 12px; }
.image-hd-actions button { min-height: 40px; padding: 10px 20px; border: 1px solid #dee0e3; border-radius: 10px; background: #fff; color: #35393e; font: inherit; font-size: 14px; cursor: pointer; }
.image-hd-actions button.primary { background: #2b7cf6; border-color: #2b7cf6; color: #fff; font-weight: 500; }
@media (max-width: 560px) { .image-hd-actions { flex-direction: column-reverse; } .image-hd-actions button { width: 100%; } }
</style>
